import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import type { TrailMapHandle } from './TrailMap';
import TrailMap from './TrailMap';

import { ErrorBoundary } from 'magical-trails-react';

import { RootState } from '../../store';
import getCode from '../../utils/getCode';

import ModalRouter from '../ModalRouter/ModalRouter';
import TrailPoints from '../TrailPoints';

// import { generatMarkerIcon } from './icon';
import './styles/index.scss';
import Loading from '../Loading';
import TrailMenu from './TrailMenu';
import TrailFooter from './TrailFooter';
import MapButtons from './MapButtons';
import {
  clearApiError,
  removePointFromScannedPointQueue,
} from '../../store/trail';
import GDPR from '../GDPR/GDPR';
import preloadImages from '../../utils/preloadImages';
import { preloadOpenCV } from '../../utils/compareImages';
import InAppBrowserWarning from '../GDPR/InAppBrowserWarning';
import ErrorScreen from '../ErrorScreen';
import {
  isVisionMatchPage,
  messageDeliveryFor,
  resolvePhoneShell,
} from 'magical-trails';
import useLiveTrailUpdates from '../../hooks/useLiveTrailUpdates';
import usePlayerTheme from '../../hooks/usePlayerTheme';
import useLeaveTrailGuard from '../../hooks/useLeaveTrailGuard';
import LeaveTrailDialog from './LeaveTrailDialog';
import PhoneDock from '../Phone/PhoneDock';
import PhoneOverlays from '../Phone/PhoneOverlays';
import { isTrailView } from '../Phone/phoneRoutes';
import usePhoneController from '../Phone/usePhoneController';

export interface TrailParams {
  trailId: string;
}

const Trail: React.FC = (): JSX.Element => {
  const dispatch = useDispatch();
  let { trailId } = useParams<TrailParams>();
  let history = useHistory();
  const location = useLocation();
  const [selectedPoint, setSelectedPoint] = useState<string>('');
  const [scrollSnapEnabled, setScrollSnapEnabled] = useState<boolean>(true);
  const [clickId, setClickId] = useState<number>(0);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const maplibreRef = useRef<TrailMapHandle | null>(null);
  // Previously a module-level `let timeout`, shared across every Trail
  // instance/mount and also cleared by an UNRELATED effect's cleanup below
  // (the scannedPointQueue effect), so a rapid-fire pauseScrollSnap() call
  // could stack orphaned timers and/or have its pending re-enable cancelled
  // by something that had nothing to do with scroll-snap. A ref scoped to
  // this component instance, cleared before re-arming, fixes both.
  const scrollSnapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const pauseScrollSnap = () => {
    // Nothing to pause while a modal route is open over the trail.
    if (!isTrailView(location.pathname)) return;
    setScrollSnapEnabled(false);
    if (scrollSnapTimeoutRef.current) {
      clearTimeout(scrollSnapTimeoutRef.current);
    }
    scrollSnapTimeoutRef.current = setTimeout(() => {
      setScrollSnapEnabled(true);
      scrollSnapTimeoutRef.current = null;
    }, 750);
  };

  // Dedicated unmount cleanup for the scroll-snap timer — separate from any
  // other effect so it can never be cleared by an unrelated effect re-running.
  useEffect(() => {
    return () => {
      if (scrollSnapTimeoutRef.current) {
        clearTimeout(scrollSnapTimeoutRef.current);
      }
    };
  }, []);

  const handleSetSelectedPoint = (selectedPoint: string) => {
    pauseScrollSnap();
    setSelectedPoint(selectedPoint);
    setClickId(Math.random());
  };

  const {
    trailLoaded,
    currentStory,
    currentTrail,
    scannedPointQueue,
    loading,
    storySelector,
    apiError,
    inviteRequired,
  } = useSelector((state: RootState) => state.trail);

  const { consentDecided, inAppBrowser } = useSelector(
    (state: RootState) => state.ui
  );

  const showFooter = !!currentStory?.config?.showCodeEntry;

  // The story's look, applied to the document (see the hook).
  usePlayerTheme(currentStory?.config?.playerTheme);

  // Keep the open trail in step with the author's edits (see the hook).
  useLiveTrailUpdates(currentStory?.code);

  // Catch the phone's back button before it drops the player out of the
  // trail (see the hook). With the menu open, back just closes the menu.
  const leaveGuard = useLeaveTrailGuard({
    active: trailLoaded,
    onBack: () => {
      if (!menuOpen) return false;
      setMenuOpen(false);
      return true;
    },
  });

  // The phone shell (docs/PHONE-SHELL.md): null for every story that doesn't
  // ask for it, in which case nothing below changes.
  const phoneShell = useMemo(
    () => resolvePhoneShell(currentStory?.config),
    [currentStory?.config]
  );
  const openMenu = useCallback(() => setMenuOpen(true), []);
  const phone = usePhoneController(phoneShell, currentStory, {
    openMenu,
    selectPoint: (code) => handleSetSelectedPoint(code),
  });

  // Memoize the filtered map pins so they only update when currentStory changes.
  const mapPins = useMemo(() => {
    return (
      currentStory?.storyPoints.filter(
        (point) =>
          point.type === 'location' &&
          point.status !== 'hidden' &&
          // A stop the author removed after the player finished it stays on
          // the list as done, but its poster is gone: no pin to walk to.
          !point.removedFromTrail &&
          !(
            point.status === 'completed' &&
            point.config?.hideLocationPinOnComplete === true
          )
      ) || []
    );
  }, [currentStory]);

  // Memoize the points for TrailMap to prevent re-triggering bounds useEffect on unrelated re-renders
  const trailMapPoints = useMemo(() => {
    return mapPins
      .filter((p: any) => p.type === 'location' && (p as any).latlng)
      .map((p: any) => ({
        id: p.code || p.id,
        code: p.code,
        coordinates: [
          (p as any).latlng?.[1] ?? (p as any).lng,
          (p as any).latlng?.[0] ?? (p as any).lat,
        ] as [number, number],
        status: p.status,
      }));
  }, [mapPins]);

  const geofencedPoints = useMemo(() => {
    return trailMapPoints.filter((p) => p.status === 'geofenced');
  }, [trailMapPoints]);

  useEffect(() => {
    // If exactly one location is visible and we haven't selected it yet
    if (mapPins.length === 1 && selectedPoint !== mapPins[0].code) {
      // store timer so we can clear if needed
      const timer = setTimeout(() => {
        handleSetSelectedPoint(mapPins[0].code || '');
      }, 600); // short delay to let the list render

      return () => clearTimeout(timer);
    }
  }, [mapPins, selectedPoint]);

  useEffect(() => {
    if (
      trailLoaded &&
      isTrailView(location.pathname) &&
      scannedPointQueue.length > 0
    ) {
      const code = scannedPointQueue[0];
      dispatch(removePointFromScannedPointQueue());
      getCode(code, history);
      handleSetSelectedPoint(code);
    }
  }, [location, scannedPointQueue, trailLoaded]);

  useEffect((): void => {
    const selectedPointData = currentStory?.storyPoints.find(
      (point) => point.code === selectedPoint
    );
    const m = maplibreRef.current?.getMap();
    if (selectedPointData && (selectedPointData as any)?.latlng && m) {
      const [lat, lng] = (selectedPointData as any).latlng as [number, number];
      try {
        m.easeTo({ center: [lng, lat], zoom: Math.max(m.getZoom(), 18) });
      } catch {}
    }
  }, [selectedPoint, clickId, currentStory]);

  // After an AR session the map can wake up mis-measured: during immersive
  // AR the whole trail is visibility-hidden (see the xr-presenting styles)
  // while Chrome's fullscreen churn resizes the viewport, and MapLibre
  // measures a hidden container as 0x0 — leaving the canvas transform stale
  // when the trail reappears, so taps land on the wrong coordinates ("map
  // unresponsive after closing AR" in field testing, until a manual page
  // refresh). Kick a resize whenever the player lands back on the base map
  // route and whenever fullscreen toggles.
  useEffect(() => {
    const kick = () => {
      try {
        maplibreRef.current?.getMap()?.resize();
      } catch {
        // Map not ready yet — nothing to fix.
      }
    };
    if (isTrailView(location.pathname)) kick();
    document.addEventListener('fullscreenchange', kick);
    // fullscreenchange can fire while `.Trail` is STILL visibility-hidden
    // (the AR page removes html.xr-presenting on unmount, which may come a
    // beat later) — a resize measured then is 0x0 all over again, which is
    // the "sometimes" in "sometimes still unresponsive". So also kick the
    // moment the class actually comes off, once layout has caught up.
    let raf = 0;
    const html = document.documentElement;
    let wasPresenting = html.classList.contains('xr-presenting');
    const observer = new MutationObserver(() => {
      const presenting = html.classList.contains('xr-presenting');
      if (wasPresenting && !presenting) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => requestAnimationFrame(kick));
      }
      wasPresenting = presenting;
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => {
      document.removeEventListener('fullscreenchange', kick);
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
    // trailLoaded re-runs the kick once the map actually exists — at first
    // render the ref is still empty and the mount-time kick would no-op.
  }, [location.pathname, trailLoaded]);

  // Stable signature for the effect below: `currentStory` (and the
  // `mapPins` memo derived from it) get a new object/array reference on
  // EVERY progress dispatch (viewing a page, scanning a code, answering a
  // question, ...), since the Redux slice rebuilds `currentStory.storyPoints`
  // on each of those actions. Depending on those references directly re-ran
  // the camera refit (yanking the map away from wherever the visitor had
  // panned/zoomed it) and re-issued the FULL asset preload on every single
  // progress update. Story code + the joined set of visible pin codes only
  // change when the pins actually change, not on every progress tick.
  const mapPinsSignature = mapPins.map((p) => p.code).join(',');

  useEffect(() => {
    if (currentStory && mapPins.length > 0) {
      zoomToFit();
      preloadImages(currentStory.storyPoints);
    } else if (currentStory && phoneShell && currentTrail?.points.length) {
      // A phone-shell story often opens with every poster still hidden.
      // With no pin to fit, the map would sit on its whole-world default;
      // look at the trail's patch instead, without marking where anything is.
      const lats = currentTrail.points.map((point) => point.lat);
      const lngs = currentTrail.points.map((point) => point.lng);
      try {
        maplibreRef.current?.getMap()?.jumpTo({
          center: [
            (Math.min(...lngs) + Math.max(...lngs)) / 2,
            (Math.min(...lats) + Math.max(...lats)) / 2,
          ],
          zoom: 15.5,
        });
      } catch {
        // Map not ready yet; the player can still pan to it.
      }
      preloadImages(currentStory.storyPoints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStory?.code, mapPinsSignature]);

  // Kick off the ~10 MB OpenCV dynamic import (see
  // src/utils/compareImages.ts) as soon as a trail with a visionMatch page
  // loads — while the visitor is presumably online — rather than waiting
  // until they reach the scan step (which could easily happen offline, deep
  // in the trail). Checked across every story in the loaded trail (not just
  // currentStory) so that switching to a different story later — including
  // offline — still has OpenCV cached, since the visitor may not have been on
  // the visionMatch story at trail-load time. The SW's version-keyed
  // CacheFirst route then keeps it available for the actual scan even if the
  // visitor goes offline after this point. preloadOpenCV()'s internal
  // cvPromise memoization makes re-firing this on every currentTrail update a
  // no-op after the first successful fetch. Best-effort: a rejection here
  // (e.g. offline at trail-load) is swallowed — the scan-time compareImages()
  // call will simply retry the import itself, and VisionMatch's "Skip
  // Scanning" affordance covers a sustained failure.
  useEffect(() => {
    const hasVisionMatch = currentTrail?.stories.some((story) =>
      story.storyPoints.some((point) => point.pages?.some(isVisionMatchPage))
    );
    if (hasVisionMatch) {
      preloadOpenCV().catch(() => {});
    }
  }, [currentTrail]);

  // Fetch the trail from the route code. Runs in an effect (not during render)
  // so an apiError — which leaves loading:false/trailLoaded:false — does not
  // re-fire getCode on every render and spin forever. The apiError guard means
  // a failure stays failed until an explicit Retry clears it (which re-runs
  // this effect because apiError is a dependency). inviteRequired is guarded
  // the same way: setInviteRequired (an invite-only/unclaimed trail) also
  // leaves loading:false/trailLoaded:false, so without a guard the fetch
  // effect would refire getCode forever behind the InviteGate overlay.
  //
  // The guard is scoped to inviteRequired.code, not just its presence: the
  // `/trail/:code` route reuses this same mounted Trail component when only
  // the code param changes (e.g. scanning a second, different invite-only
  // trail without navigating away first), so a stale inviteRequired left over
  // from a PREVIOUS code must not block the fetch for a NEW one — getCode
  // always dispatches setInviteRequired with the exact code it was called
  // with (see loadJsonFromCode in utils/getCode.ts), so it is safe to compare
  // directly against the current trailId.
  //   - Same code, still invite-gated: inviteRequired.code === trailId stays
  //     true, so the block condition holds and getCode never refires — the
  //     loop stays fixed.
  //   - trailId changes to a different code: inviteRequired.code (from the
  //     old trail) no longer equals the new trailId, so the block condition
  //     is false and this effect fires getCode for the new code exactly
  //     once. If THAT code is also invite-gated, setInviteRequired updates
  //     inviteRequired to match the new trailId, and the guard re-engages for
  //     it.
  useEffect(() => {
    if (
      !trailLoaded &&
      !loading &&
      !storySelector &&
      !apiError &&
      !(inviteRequired && inviteRequired.code === trailId)
    ) {
      getCode(trailId, history);
    }
  }, [trailId, trailLoaded, loading, storySelector, apiError, inviteRequired]);

  const zoomToFit = () => {
    const m = maplibreRef.current?.getMap();
    if (!m || !mapPins.length) return;
    let minLng = Infinity,
      minLat = Infinity,
      maxLng = -Infinity,
      maxLat = -Infinity;
    mapPins
      .filter((p: any) => p.latlng)
      .forEach((p: any) => {
        const [lat, lng] = p.latlng as [number, number];
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      });
    if (
      isFinite(minLng) &&
      isFinite(minLat) &&
      isFinite(maxLng) &&
      isFinite(maxLat)
    ) {
      // Padding: more bottom space for trail points, right for buttons
      maplibreRef.current?.fitToBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { top: 40, left: 40, right: 80, bottom: 200 }
      );
      setSelectedPoint('');
    }
  };

  if (!trailLoaded) {
    if (apiError) {
      return (
        <ErrorScreen
          onRetry={() => dispatch(clearApiError())}
          onHome={() => {
            dispatch(clearApiError());
            history.push('/');
          }}
        />
      );
    }
    return <Loading />;
  }

  return (
    <>
      {/* Under a phone screen the map and list are still in the page: keep
          them out of the tab order and away from screen readers. */}
      <div className="Trail" inert={phone?.covering || undefined}>
        {trailLoaded && currentStory && mapPins && (
          <ErrorBoundary
            fallback={
              <div className="Map p-8">
                <p>An error occured, please refresh the page.</p>
              </div>
            }
          >
            <TrailMap
              ref={maplibreRef as any}
              className="Map"
              points={trailMapPoints}
              selectedPointId={selectedPoint}
              onMarkerClick={(id: string) => handleSetSelectedPoint(id)}
              showUserLocation={true}
              showAccuracyCircle={false}
              showGeofence={geofencedPoints.length > 0}
              geofencePoints={geofencedPoints}
              geofenceRadiusMeters={10}
            />
            <MapButtons
              zoomIn={() => maplibreRef.current?.zoomIn()}
              zoomOut={() => maplibreRef.current?.zoomOut()}
              fit={() => zoomToFit()}
              openMenu={
                showFooter || phone ? undefined : () => setMenuOpen(true)
              }
            />
            <TrailPoints
              selectedPoint={selectedPoint}
              points={currentStory?.storyPoints.filter(
                (storyPoint) =>
                  storyPoint.status !== 'hidden' &&
                  // In the phone shell a message lives in its thread, not
                  // on the list under the map.
                  !messageDeliveryFor(storyPoint, currentStory.config)
              )}
              onSelect={(pointId: string): void => {
                handleSetSelectedPoint(pointId);
              }}
              showDirections={
                currentStory.config?.showDirectionsButtonOnLocationPoints
              }
              scrollSnapEnabled={
                isTrailView(location.pathname) && scrollSnapEnabled
              }
              pauseScrollSnap={pauseScrollSnap}
            />
          </ErrorBoundary>
        )}
        {phone?.shell.layout === 'dock' ? (
          <PhoneDock shell={phone.shell} apps={phone.apps} />
        ) : (
          showFooter &&
          !phone && <TrailFooter openMenu={() => setMenuOpen(true)} />
        )}
      </div>
      {phone && <PhoneOverlays phone={phone} />}
      <ModalRouter
        selectedPoint={selectedPoint}
        setSelectedPoint={setSelectedPoint}
      />
      <TrailMenu
        open={menuOpen}
        setOpen={setMenuOpen}
        showMessageSounds={Boolean(phone)}
      />
      <LeaveTrailDialog
        open={leaveGuard.confirmOpen}
        stay={leaveGuard.stay}
        leave={leaveGuard.leave}
      />
      {!consentDecided && <GDPR />}
      {consentDecided && inAppBrowser && <InAppBrowserWarning />}
    </>
  );
};

export default Trail;
